/**
 * navConfig.js - Centralised admin navigation configuration.
 * Used by AdminLayout, Sidebar, and TopNav.
 */
import {
  LayoutDashboard, FileText, Tag, Settings, Users, Bot, ClipboardList,
  BarChart3, Mail, Key, DollarSign, Kanban, AlertTriangle, Wrench, Package,
  UserCircle, Activity, SlidersHorizontal, Shield, HardDrive, BoxSelect,
  ListChecks, PanelLeftClose, PanelLeft, MonitorSmartphone, Database, Zap,
  BookOpen, CalendarDays, Briefcase, FolderOpen, ScrollText, Printer, Trash2,
  MessageSquare, Receipt, Github, Smartphone, MessageCircle, MapPin, BellRing, Send,
  Warehouse, ShoppingCart, Coins, Phone, Wand2, GitBranch, ShieldAlert, Sparkles, Code,
  Building2,
} from "lucide-react";

// iter413c-17 — Reorganized sidebar taxonomy (8 logical groups).
// Goal: easier to scan with 50+ admin items, related modules co-located.
export const NAV_GROUPS = [
  // ─── 1. Hlavní — daily workspace ─────────────────────────────────────
  { key: "main", label: "Hlavní", icon: LayoutDashboard, defaultOpen: true, items: [
    { path: "/admin/dashboard",      label: "Dashboard",           icon: LayoutDashboard, moduleKey: "dashboard" },
    { path: "/admin/calendar",       label: "Kalendář",            icon: CalendarDays,    moduleKey: "calendar" },
    { path: "/admin/tasks",          label: "Úkoly",               icon: ListChecks,      moduleKey: "tasks" },
    { path: "/admin/chat",           label: "Chat",                icon: MessageCircle,   moduleKey: "chat" },
    { path: "/admin/emails",         label: "E-maily",             icon: Mail,            moduleKey: "emails" },
    { path: "/admin/phone-history",  label: "Telefonní historie",  icon: Phone,           moduleKey: "phone_history" },
    { path: "/admin/activity",       label: "Aktivita",            icon: Activity,        moduleKey: "activity" },
    { path: "/admin/timesheets",     label: "Výkazy práce",        icon: ClipboardList,   moduleKey: "timesheets" },
  ]},

  // ─── 2. CRM & Obchod — sales pipeline ────────────────────────────────
  { key: "business", label: "CRM & Obchod", icon: Briefcase, defaultOpen: true, items: [
    { path: "/admin/submissions",     label: "Poptávky",      icon: FileText,    moduleKey: "submissions" },
    { path: "/admin/customers",       label: "Zákazníci",     icon: UserCircle,  moduleKey: "customers" },
    { path: "/admin/quotes",          label: "Nabídky",       icon: DollarSign,  moduleKey: "quotes" },
    { path: "/admin/nabidky2",        label: "Nabídky 2",     icon: Sparkles,    moduleKey: "quotes",   badge: "NOVÉ" },
    { path: "/admin/projects",        label: "Projekty",      icon: Kanban,      moduleKey: "projects" },
    { path: "/admin/invoices",        label: "Fakturace",     icon: Receipt,     moduleKey: "invoices" },
  ]},

  // ─── 3. E-shop & Katalog — products & online store ──────────────────
  { key: "eshop", label: "E-shop & Katalog", icon: ShoppingCart, items: [
    { path: "/admin/items-catalog",   label: "Katalog položek", icon: Package,       moduleKey: "items_catalog" },
    { path: "/admin/manufacturers",   label: "Výrobci",         icon: Building2,     moduleKey: "items_catalog" },
    { path: "/admin/photo-library",   label: "Knihovna fotek",  icon: Package,       moduleKey: "items_catalog" },
    { path: "/admin/categories",      label: "Kategorie",       icon: Tag,           moduleKey: "categories" },
  ]},

  // ─── 4. Sklad & Logistika — WMS + purchasing ─────────────────────────
  { key: "warehouse", label: "Sklad & Logistika", icon: Warehouse, items: [
    { path: "/admin/warehouse",       label: "Sklad (WMS)",       icon: Warehouse,     moduleKey: "warehouse" },
    { path: "/admin/purchasing",      label: "Nákup",             icon: ShoppingCart,  moduleKey: "warehouse" },
    { path: "/admin/rentals",         label: "Půjčovna",          icon: BoxSelect,     moduleKey: "rentals" },
    { path: "/admin/quote-defaults",  label: "Auto-fill položek", icon: Sparkles,      adminOnly: true, moduleKey: "quotes" },
  ]},

  // ─── 5. Servis & Inspekce — service, claims, revisions ──────────────
  { key: "service", label: "Servis & Inspekce", icon: Wrench, items: [
    { path: "/admin/claims",          label: "Reklamace",       icon: AlertTriangle, moduleKey: "claims" },
    { path: "/admin/service",         label: "Servis",          icon: Wrench,        moduleKey: "service" },
    { path: "/admin/revize",          label: "Revize",          icon: ClipboardList, moduleKey: "revize",         badge: "ČSN" },
    { path: "/admin/penb",            label: "PENB",            icon: BarChart3,     moduleKey: "penb" },
    { path: "/admin/installations",   label: "Mapa instalací",  icon: MapPin,        moduleKey: "installations" },
    { path: "/admin/professionals",   label: "Řemeslníci",      icon: Users,         moduleKey: "professionals" },
  ]},

  // ─── 6. Dokumenty & Šablony — documents, contracts, templates ───────
  { key: "docs", label: "Dokumenty & Šablony", icon: FolderOpen, items: [
    { path: "/admin/documents",       label: "Dokumenty",     icon: FileText,    moduleKey: "documents" },
    { path: "/admin/files",           label: "Soubory",       icon: HardDrive,   moduleKey: "files" },
    { path: "/admin/protocols",       label: "Protokoly",     icon: ScrollText,  moduleKey: "protocols" },
    { path: "/admin/contracts",       label: "Smlouvy",       icon: FileText,    moduleKey: "contracts" },
    { path: "/admin/subsidies",       label: "Dotace",        icon: Zap,         moduleKey: "subsidies" },
    { path: "/admin/templates",       label: "Šablony",       icon: BookOpen,    moduleKey: "templates" },
    { path: "/admin/email-templates", label: "E-mail šablony", icon: Mail,       moduleKey: "email_templates" },
    { path: "/admin/pdf-editor",      label: "PDF Editor",    icon: Printer,     moduleKey: "pdf_editor" },
    { path: "/admin/templates-hub",   label: "Centrum šablon", icon: FileText,   adminOnly: true, moduleKey: "pdf_editor" },
    { path: "/admin/html-templates",  label: "HTML šablony",  icon: Code,        adminOnly: true, moduleKey: "pdf_editor" },
  ]},

  // ─── 7. Marketing & AI — automation, AI, comms ───────────────────────
  { key: "marketing", label: "Marketing & AI", icon: Sparkles, items: [
    { path: "/admin/automation",     label: "Automatizace",   icon: GitBranch,     moduleKey: "automation" },
    { path: "/admin/ai-agent",       label: "AI Asistent",    icon: Bot,           moduleKey: "ai_agent" },
    { path: "/admin/ai-analytics",   label: "AI Analytika",   icon: BarChart3,     moduleKey: "ai_analytics" },
    { path: "/admin/ai-budget",      label: "AI Budget",      icon: Coins,         adminOnly: true, moduleKey: "ai_budget" },
    { path: "/admin/whatsapp",       label: "WhatsApp Agent", icon: MessageCircle, moduleKey: "whatsapp" },
    { path: "/admin/sms",            label: "SMS",            icon: MessageSquare, moduleKey: "sms" },
    { path: "/admin/push-templates", label: "Push šablony",   icon: BellRing,      adminOnly: true, moduleKey: "mobile_api" },
    { path: "/admin/system-messages", label: "System Messages", icon: Send,        adminOnly: true, moduleKey: "system_messages" },
  ]},

  // ─── 8. Administrace — users, roles, licences, system ────────────────
  { key: "admin", label: "Administrace", icon: Shield, adminOnly: true, items: [
    { path: "/admin/users",                label: "Uživatelé",             icon: Users,           adminOnly: true, moduleKey: "users" },
    { path: "/admin/roles",                label: "Role a oprávnění",      icon: Shield,          adminOnly: true, moduleKey: "roles" },
    { path: "/admin/licences",             label: "Licence",               icon: Sparkles,        adminOnly: true, moduleKey: "licences",          badge: "NOVÉ" },
    { path: "/admin/anomalies",            label: "Anomálie & Alerty",     icon: ShieldAlert,     adminOnly: true, moduleKey: "settings",          badge: "NOVÉ" },
    { path: "/admin/master-data-health",   label: "Master Data Health",    icon: Activity,        adminOnly: true, moduleKey: "settings" },
    { path: "/admin/api-keys",             label: "API klíče",             icon: Key,             adminOnly: true, moduleKey: "api_keys" },
    { path: "/admin/config-wizard",        label: "Konfigurační průvodce", icon: Wand2,           adminOnly: true, moduleKey: "api_keys" },
    { path: "/admin/demo-data",            label: "Demo data",             icon: Database,        adminOnly: true, moduleKey: "demo_data" },
    { path: "/admin/data-management",      label: "Import / Export",       icon: HardDrive,       adminOnly: true, moduleKey: "data_management" },
    { path: "/admin/audit-log",            label: "Audit log",             icon: ClipboardList,   adminOnly: true, moduleKey: "audit_log" },
    { path: "/admin/server-diagnostics",   label: "Diagnostika",           icon: Activity,        adminOnly: true, moduleKey: "server_diagnostics" },
    { path: "/admin/diagnostics",          label: "Datová diagnostika",    icon: ShieldAlert,     adminOnly: true, moduleKey: "server_diagnostics" },
    { path: "/admin/client-errors",        label: "Klientské chyby",       icon: ShieldAlert,     adminOnly: true, moduleKey: "api_keys" },
    { path: "/admin/mobile-api",           label: "Mobilní API",           icon: Smartphone,      adminOnly: true, moduleKey: "mobile_api" },
    { path: "/admin/system-updates",       label: "Aktualizace systému",   icon: Github,          adminOnly: true, moduleKey: "system_updates" },
    { path: "/admin/settings",             label: "Nastavení",             icon: Settings,        moduleKey: "settings" },
    { path: "/admin/advanced",             label: "Pokročilé nastavení",   icon: SlidersHorizontal, moduleKey: "advanced" },
    { path: "/admin/trash",                label: "Koš",                   icon: Trash2,          moduleKey: "trash" },
  ]},
];

// Flat list for topnav layout and filtering
export const ALL_NAV_ITEMS = NAV_GROUPS.flatMap(g =>
  [{ section: "divider" }, ...g.items]
).slice(1);

export const LAYOUT_OPTIONS = [
  { key: "sidebar", label: "Sidebar", icon: PanelLeft, desc: "Klasicky postranni panel" },
  { key: "compact", label: "Kompaktni", icon: PanelLeftClose, desc: "Ikony bez textu" },
  { key: "topnav", label: "Horni menu", icon: MonitorSmartphone, desc: "Navigace nahore" },
];

export const SPACING_DENSITY = [
  { key: "compact", label: "Kompaktni", padding: "p-2 sm:p-4" },
  { key: "normal", label: "Normalni", padding: "p-3 sm:p-8" },
  { key: "spacious", label: "Vzdusny", padding: "p-4 sm:p-12" },
];

export function getSpacingDensity() {
  try { return localStorage.getItem("global_spacing") || "normal"; } catch { return "normal"; }
}
export function setSpacingDensity(v) { localStorage.setItem("global_spacing", v); }

// Filtered nav items
export function getVisibleNav(user, visibleModules) {
  // iter413c-13 — Modules always visible (dashboard + personal settings).
  const ALWAYS_VISIBLE_FOR_ADMIN = new Set([
    "dashboard", "settings", "advanced", "trash",
  ]);
  const tenantActiveModules = user?.tenant_active_modules || null;
  const isTenantScoped = !!user?.tenant_id && user?.role !== "super_admin";

  return ALL_NAV_ITEMS.filter(item => {
    if (item.section) return true;
    if (item.adminOnly && user?.role !== "admin" && user?.role !== "super_admin") return false;
    if (visibleModules && !visibleModules.includes(item.moduleKey)) return false;
    // iter413c-13 — Tenant licence-based visibility
    if (isTenantScoped && Array.isArray(tenantActiveModules)) {
      if (!ALWAYS_VISIBLE_FOR_ADMIN.has(item.moduleKey) && !tenantActiveModules.includes(item.moduleKey)) {
        return false;
      }
    }
    if (user?.allowed_modules?.length > 0 && !user.allowed_modules.includes(item.moduleKey) && item.moduleKey !== "dashboard" && item.moduleKey !== "settings") return false;
    return true;
  }).filter((item, idx, arr) => {
    if (item.section === "divider") {
      if (idx === 0 || idx === arr.length - 1) return false;
      if (arr[idx - 1]?.section === "divider") return false;
    }
    return true;
  });
}

export const PERMANENT_LOGO = "/solar-green-logo.png";
